import {
  IAddressResponseData,
  ApiError,
  IAddressData,
  IPaymentData,
  PaymentFactory,
} from "../../../../typescript-library";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import genericStyles from "../../../lib/GenericStyles.module.scss";
import {getShopId} from "../../../lib/getShopId";
import {IPaymentModule, ISubmitPaymentResponse} from "../../../lib/payments/IPaymentModule";
import {frcErrorMessage, frcSiteKey, PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {
  CheckoutError,
  CheckoutStep,
  DEFAULT_ERROR_MESSAGE,
  IIsVisible,
  prepareTrackingProductData,
} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {findRootError} from "../Summary/summaryErrors";
import {captureSentryException} from "../../../../helpers/sentryHelper";
import PaymentRow from "./PaymentRow";
import {isSameAddress} from "../AddressStep/AddressStep";
import {useCustomerSelector} from "../../../../store";
import {Tracking} from "../../../../tracking/tracking";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {
  FRCWidgetCompleteEvent,
  FRCWidgetWidgetErrorEvent,
  FRCWidgetWidgetExpireEvent,
  WidgetHandle,
} from "@friendlycaptcha/sdk";

interface IPaymentContext {
  creditCardImageRef: React.MutableRefObject<HTMLDivElement | null>;
  frcContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  dateOfBirthRef: React.MutableRefObject<string | null>;
  isPaymentLoading: boolean;
}

interface IRequestState {
  buttonType: ButtonType;
  buttonValue: string | JSX.Element;
  errors: (string | JSX.Element)[];
  failedPaymentId?: number;
}

const paymentContext = React.createContext<IPaymentContext | null>(null);

export const usePaymentContext = (): IPaymentContext => {
  const context = React.useContext(paymentContext);
  if (!context) {
    throw new Error("not into component");
  }

  return context;
};

const Payment: React.FC<IIsVisible> = (props) => {
  const {customer} = useCustomerSelector();
  const {frcSdk, braintreeData} = useCartContext();
  const {checkout} = useCheckoutContext();
  const {gotoStep} = useCheckoutViewContext();
  const {i18n, t} = useTranslation(["errorcodes", "translations"]);
  const [paymentOptions, setPaymentOptions] = useState<IPaymentData[]>([]);
  const [frcWidget, setFrcWidget] = useState<WidgetHandle | null>(null);
  const frcWidgetRef = React.useRef<WidgetHandle | null>(null);
  frcWidgetRef.current = frcWidget;
  const [frcResponseToken, setFrcResponseToken] = useState<string | null>(null);

  const [preferredPaymentId, setPreferredPaymentId] = useState<number>();
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(null);
  const isContinueInitiallyBlocked =
    !checkout.paymentId || (checkout.paymentId as Payments) === Payments.CreditCardBraintree;
  const buttonText = t("checkout.toSummary");
  const [requestState, setRequestState] = useState<IRequestState>({
    buttonType: isContinueInitiallyBlocked ? ButtonType.disabled : ButtonType.default,
    buttonValue: buttonText,
    errors: [],
  });
  // This ref is used in PaymentBirthdayPicker.tsx
  const dateOfBirthRef = useRef<string>(null);
  const creditCardImageRef = useRef<HTMLDivElement>(null);
  const frcContainerRef = useRef<HTMLDivElement>(null);
  const previousDataRef = useRef<{
    deliveryAddress: IAddressResponseData | null;
    invoiceAddress: IAddressResponseData | null;
    oldPayableAmount: string;
  }>({
    deliveryAddress: null,
    invoiceAddress: null,
    oldPayableAmount: "null",
  });
  const inputCardNo = useRef<HTMLInputElement>(null);

  /**
   * request payment methods
   */
  const paymentIdsToBeFiltered = [
    Payments.Paypal,
    Payments.SEPA,
    Payments.CreditCard,
    Payments.AmazonPay,
    Payments.PaypalExpressBraintree,
    Payments.ApplePay,
    Payments.GooglePay,
  ];

  useEffect(() => {
    const {invoiceAddress, deliveryAddress, oldPayableAmount} = previousDataRef.current;
    const payableAmount = checkout.cartData.value.payable.amount;

    previousDataRef.current = {
      deliveryAddress: checkout.deliveryAddress,
      invoiceAddress: checkout.invoiceAddress,
      oldPayableAmount: payableAmount,
    };

    if (
      isSameAddress(invoiceAddress, checkout.invoiceAddress) &&
      isSameAddress(deliveryAddress, checkout.deliveryAddress) &&
      oldPayableAmount === payableAmount
    ) {
      return;
    }

    if (checkout.cartData.positions.find((pos) => pos.item?.itemClassId === 6) && !customer.isLoggedIn) {
      paymentIdsToBeFiltered.push(Payments.Invoice);
    }

    if ([Payments.PaypalExpressBraintree, Payments.ApplePay, Payments.GooglePay].includes(checkout.paymentId || 0)) {
      return;
    }

    void PaymentFactory.getPaymentMethods(getShopId(), checkout.checkoutId).then((tempAvailable) => {
      const availables = tempAvailable.filter(
        (temp) => !paymentIdsToBeFiltered.includes(temp.paymentId) && temp.paymentAvailability.isAvailable,
      );

      const paymentInAvailables = availables.find(
        (available) =>
          available.paymentId === PaymentController.getPaymentId() || available.paymentId === checkout.paymentId,
      )?.paymentId;

      const tempPreferredPaymentId =
        availables.find((available) => available.isCustomerPreferredPayment)?.paymentId || Payments.PaypalBraintree;

      const paymentIdToSet = paymentInAvailables || tempPreferredPaymentId;

      setPreferredPaymentId(tempPreferredPaymentId);
      setPaymentModule(PaymentController.createPayment(checkout, paymentIdToSet));

      const availablesWithPayPalFirst = [
        ...availables.filter((available) => (available.paymentId as Payments) === Payments.PaypalBraintree),
        ...availables.filter((available) => (available.paymentId as Payments) !== Payments.PaypalBraintree),
      ];
      setPaymentOptions(availablesWithPayPalFirst);
    });
  }, [checkout.invoiceAddress, checkout.deliveryAddress, checkout.cartData.value.payable.amount]);

  const onFrcWidgetComplete = useCallback((event: FRCWidgetCompleteEvent) => {
    setFrcResponseToken(event.detail.response);
  }, []);

  /* eslint-disable no-console */
  const onFrcWidgetError = useCallback((event: FRCWidgetWidgetErrorEvent) => {
    console.error("FRC ran into an error:", event.detail.error);
  }, []);

  const onFrcWidgetExpired = useCallback((event: FRCWidgetWidgetExpireEvent) => {
    if (__DEVELOPMENT__) console.warn("The widget expired because the user waited too long", event);
    frcWidgetRef.current?.reset();
    setFrcResponseToken(null);
  }, []);
  /* eslint-enable no-console */

  const removeEventListeners = () => {
    frcWidgetRef.current?.removeEventListener("frc:widget.complete", onFrcWidgetComplete);
    frcWidgetRef.current?.removeEventListener("frc:widget.error", onFrcWidgetError);
    frcWidgetRef.current?.removeEventListener("frc:widget.expire", onFrcWidgetExpired);
  };

  useEffect(() => removeEventListeners, []);

  /**
   * init inputs in payment row
   */
  useEffect(() => {
    if (!props.isVisible) {
      if (frcWidget) {
        removeEventListeners();
        frcWidget?.destroy();
        setFrcWidget(null);
      }

      setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: []}));

      return;
    }

    if (!paymentModule) {
      return;
    }

    setRequestState((prevState) => ({...prevState, buttonType: ButtonType.disabled}));

    if (PaymentController.getPaymentId() === Payments.CreditCardBraintree && frcContainerRef.current) {
      if (frcWidget) {
        frcWidget.reset();
      } else {
        const widget = frcSdk.createWidget({
          element: frcContainerRef.current,
          formFieldName: "captcha-response",
          sitekey: frcSiteKey,
          apiEndpoint: "eu",
        });
        widget.addEventListener("frc:widget.complete", onFrcWidgetComplete);
        widget.addEventListener("frc:widget.error", onFrcWidgetError);
        widget.addEventListener("frc:widget.expire", onFrcWidgetExpired);
        widget.start();

        setFrcWidget(widget);
      }
    } else if (frcWidget) {
      removeEventListeners();
      frcWidget.destroy();
      setFrcWidget(null);
    }

    paymentModule
      .onPaymentSelect(creditCardImageRef, braintreeData)
      .then(() => {
        formatCheck();
        setRequestState((prevState) => ({...prevState, buttonType: ButtonType.default}));
      })
      .catch((err) => {
        const rootError = findRootError(err as Error);
        const tempError = ((rootError as ApiError).originalError || rootError) as Error;
        // Credit Card is the only payment, that has a select functionality, and thus can throw an error
        tempError.name = "CreditCard" + tempError.name;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        let errorMessage: string = tempError.message;
        if (i18n.exists(errorMessage, {ns: "errorcodes"})) {
          errorMessage = t(errorMessage);
        } else {
          const sentryEventId = captureSentryException(tempError, checkout, "cc hosted fields init on selection");
          errorMessage = t(DEFAULT_ERROR_MESSAGE, {sentryEventId});
        }
        setRequestState((prevState) => ({
          ...prevState,
          buttonType: ButtonType.default,
          errors: [errorMessage],
        }));
      });
  }, [paymentModule, props.isVisible]);

  /**
   * payment clicked handler
   * @param paymentId
   */
  const onPaymentClicked = (paymentId: number) => {
    setRequestState((prevState) => ({...prevState, buttonValue: <Spinner />}));
    const oldPayment = PaymentController.getPayment();
    if (oldPayment && oldPayment.setAsDeselected) {
      oldPayment.setAsDeselected();
    }
    const newPayment = PaymentController.createPayment(checkout, paymentId);
    setRequestState((prevState) => ({...prevState, errors: []}));
    // Will trigger the init input useEffect
    setPaymentModule(newPayment);
    void checkout.put({paymentId}).then(() => {
      gotoStep(CheckoutStep.PAYMENT);
      setRequestState((prevState) => ({...prevState, buttonValue: buttonText}));
    });
  };

  const reducePaymentOptions = useCallback(() => {
    const reducedPaymentOptions = paymentOptions.filter(
      (ps) => (ps.paymentId as Payments) !== Payments.CreditCardBraintree,
    );
    setPaymentOptions(reducedPaymentOptions);
    onPaymentClicked(reducedPaymentOptions[0].paymentId);
  }, [paymentOptions]);

  const formatCheck = () => {
    if (PaymentController.getPaymentId() === Payments.CreditCardBraintree && inputCardNo.current?.value) {
      // See `publishIncludesNavigation.xsl`
      if (document.body.querySelector<HTMLUListElement>("ul.main-nav-list")?.dataset.trackAshs === "true") {
        captureSentryException(new Error("ASHS Format Error"), checkout, "Payment Step Submit", {
          cardNumber: inputCardNo.current?.value.substring(0, 5) + "***",
        });
      }

      // Reset values
      if (inputCardNo.current) inputCardNo.current.value = "";

      reducePaymentOptions();

      return true;
    }

    return false;
  };

  if (!props.isVisible) {
    return null;
  }

  if (!paymentOptions.length) {
    return (
      <div className={`Shipping ${genericStyles.mobileOnlyFriendly}`}>
        <h2>{t("checkout.paymentOptions")}</h2>
        <ListLoader />
      </div>
    );
  }

  /**
   * Submit handler
   * @param evt
   */
  const submitPayment = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    if (formatCheck()) return;

    const paymentId = PaymentController.getPaymentId();
    if (!(paymentId && paymentModule)) {
      return;
    }
    const submitErrors: string[] = [];
    setRequestState((prevState) => ({...prevState, buttonValue: <Spinner />}));
    let failedPaymentId: number | undefined;
    let addressWithBirthday: {delivery: IAddressData; invoice: IAddressData} | undefined;

    const currentPayment = paymentOptions.find((available) => available.paymentId === paymentId) as IPaymentData;

    if (currentPayment.isScoringRequired) {
      if (dateOfBirthRef.current === null || dateOfBirthRef.current === "") {
        submitErrors.push(t("form.pleaseEnterBirthday"));
      }
      if (!checkout.invoiceAddress) {
        submitErrors.push(t("checkout.emptyInvoiceAddress"));
      }
      if (submitErrors.length || !checkout.invoiceAddress) {
        setRequestState((prevState) => ({
          ...prevState,
          buttonValue: buttonText,
          errors: submitErrors,
        }));

        return;
      }
      const invoiceAddressWithBirthday = {...checkout.invoiceAddress, dateOfBirth: dateOfBirthRef.current};
      addressWithBirthday = {
        delivery: invoiceAddressWithBirthday,
        invoice: invoiceAddressWithBirthday,
      };
    }

    const paymentType = currentPayment.name;
    const productData = prepareTrackingProductData(checkout.cartData);
    Tracking.sendAddPaymentInfoEvent(paymentType, productData);

    let creditCardType = "";
    try {
      let submitPaymentResponse: ISubmitPaymentResponse;
      if ((paymentId as Payments) === Payments.CreditCardBraintree) {
        if (!frcResponseToken) {
          submitPaymentResponse = {success: false, err: {code: t("FRC_NOT_RESOLVED"), cardType: ""}};
        } else {
          await PaymentFactory.getFrcVerify(frcResponseToken, frcSiteKey);
          submitPaymentResponse = await paymentModule.onSubmitPayment();
        }
      } else {
        submitPaymentResponse = await paymentModule.onSubmitPayment();
      }
      if (submitPaymentResponse.err) {
        creditCardType = submitPaymentResponse.err.cardType;
        // Special case 'American Express' in .com: show alternate wording that says, it's only possible with 'Euro' as currency
        const context =
          creditCardType === "American Express" && getShopId() === 100005 ? "AMERICAN_EXPRESS" : undefined;
        setRequestState({
          ...requestState,
          buttonValue: buttonText,
          errors: [t(submitPaymentResponse.err.code ?? "", {creditCardType, context})],
        });

        return;
      }
      await checkout.put({
        paymentId,
        address: addressWithBirthday,
      });
    } catch (submitError) {
      if ((submitError as Error).message === frcErrorMessage) {
        reducePaymentOptions();

        return;
      }

      let errorStringToPush = "";
      const tempNetworkError = ((submitError as ApiError).originalError || submitError) as {
        response?: {status: number};
        code?: string;
      } & Error;
      tempNetworkError.name = "CreditCard" + tempNetworkError.name;
      if (tempNetworkError.response?.status === 400) {
        errorStringToPush = "paymentMethodNotAvailable";
        failedPaymentId = paymentId;
      } else {
        errorStringToPush = tempNetworkError.code || tempNetworkError.message;
      }

      if (i18n.exists(errorStringToPush, {ns: "errorcodes"})) {
        submitErrors.push(t(errorStringToPush, {creditCardType}));
      } else {
        const sentryEventId = captureSentryException(tempNetworkError, checkout, "Submit payment catch block");
        submitErrors.push(t(DEFAULT_ERROR_MESSAGE, {sentryEventId}));
      }
    }
    setRequestState((prevState) => ({...prevState, buttonValue: buttonText, errors: submitErrors, failedPaymentId}));
  };

  const isPaymentLoading = requestState.buttonType === ButtonType.disabled;

  const isButtonEnabled =
    (PaymentController.getPaymentId() || !!preferredPaymentId) && typeof requestState.buttonValue === "string";

  const {Provider} = paymentContext;

  return (
    <div className={`Shipping Payment ${genericStyles.checkoutContainer} ${genericStyles.mobileOnlyFriendly}`}>
      <h2 id="headingPaymentOptions">{t("checkout.paymentOptions")}</h2>
      <CheckoutError>{requestState.errors}</CheckoutError>
      <form action="#">
        <span className="hp-field-wrapper">
          <input ref={inputCardNo} id="card-no" type="text" name="card-no" autoComplete="off" tabIndex={-1} />
        </span>
        <div role="group" className="SimpleRadioList" aria-labelledby="headingPaymentOptions">
          <Provider value={{creditCardImageRef, dateOfBirthRef, isPaymentLoading, frcContainerRef}}>
            {paymentOptions.map((payment) => (
              <PaymentRow
                failedPaymentId={requestState.failedPaymentId}
                key={payment.paymentId}
                onPaymentClicked={onPaymentClicked}
                payment={payment}
              />
            ))}
          </Provider>
        </div>
        <CartDetails />
        <Button
          styles={[ButtonStyles.wide]}
          action={submitPayment}
          type={isButtonEnabled ? requestState.buttonType : ButtonType.disabled}
        >
          {requestState.buttonValue}
        </Button>
      </form>
    </div>
  );
};

export default Payment;
